/**
 * Web Push, kept in step with the signed-in person — card #99.
 *
 * Two jobs, both invisible:
 *
 *  - **Re-register this browser's subscription** once per signed-in session.
 *    The server forgets a subscription its push service has expired, and a
 *    browser can be signed into by someone else; posting it again (an upsert
 *    on the endpoint) puts it back, under whoever is here now. It never asks
 *    for permission — only Settings does that, on a click.
 *  - **Route a notification click inside an open tab.** The service worker
 *    focuses the tab and posts the card's URL rather than reloading it, so the
 *    list someone was working in keeps its scroll and its filters.
 */
import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { http } from '@/lib/api';
import { currentSubscription, pushPermission, toRequest } from '@/lib/push';

export function PushBridge({ userId }: { userId: string }): null {
  const router = useRouter();

  useEffect(() => {
    if (pushPermission() !== 'granted') return;
    void (async () => {
      const subscription = await currentSubscription();
      if (!subscription) return;
      await http.post('/me/push/subscriptions', toRequest(subscription));
    })().catch(() => {
      // Push not configured, or offline: the reminder still arrives in-app
      // and by email, and Settings says what is wrong when someone looks.
    });
  }, [userId]);

  useEffect(() => {
    if (pushPermission() === 'unsupported') return;
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type !== 'slate:open' || typeof data.url !== 'string') return;
      const url = new URL(data.url, window.location.origin);
      if (url.origin !== window.location.origin) return;
      router.history.push(url.pathname + url.search);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [router]);

  return null;
}
